import Image from "next/image";
import Link from "next/link";
import { baseURL } from "../categories/categoryItem";
import { useAuth } from "../../contexts/AuthContext";

const ImpactListItems = ({
  assetsList,
  viewType = "desktop",
  startIndex = 0,
  walletAddress = null,
}) => {
  const { user } = useAuth();

  // Own pieces never get a buy affordance — owners see View details instead
  const isOwnedByViewer = (row) => {
    if (row.isBlockchain) {
      // Chain listings: compare the seller address to the connected wallet;
      // no wallet connected means we treat the viewer as a non-owner
      return Boolean(
        walletAddress &&
          typeof row.seller === "string" &&
          walletAddress.toLowerCase() === row.seller.toLowerCase(),
      );
    }
    if (row.isCardSale) {
      return Boolean(user && row.ownerId && user.id === row.ownerId);
    }
    return Boolean(
      user &&
        ((row.creator?.creatorId && user.id === row.creator.creatorId) ||
          (row.ownerId && user.id === row.ownerId)),
    );
  };

  const handleActionClick = (e, row) => {
    e.preventDefault();
    // Owners go to the detail page; card-sale checkout also lives on the
    // detail page. Other buy actions are unchanged.
    if (isOwnedByViewer(row) || row.isCardSale) {
      window.location.href = `/marketplace/${row._id}`;
    }
  };
  if (viewType === "mobile") {
    // Mobile Card View - Completely redesigned for mobile
    return (
      <div className="space-y-4">
        {!!assetsList?.length &&
          assetsList.map((row, index) => (
            <Link
              href={`/marketplace/${row._id}`}
              key={row._id}
              className="block bg-white dark:bg-jacarta-700 border border-jacarta-100 dark:border-jacarta-600 rounded-2xl p-4 transition-shadow duration-200 hover:shadow-md"
            >
              {/* Header with Number and Price */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-accent text-white text-sm font-bold rounded-full w-8 h-8 flex items-center justify-center">
                    #{startIndex + index + 1}
                  </div>
                  <div className="text-left">
                    <span className="text-xs text-jacarta-500 dark:text-jacarta-400 block">
                      Item
                    </span>
                    <span className="text-sm font-medium text-jacarta-700 dark:text-white">
                      Impact Art
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xs text-jacarta-500 dark:text-jacarta-400 block">
                    Price
                  </span>
                  <span className="text-lg font-bold text-accent">
                    ${row.priceUsd}
                  </span>
                </div>
              </div>

              {/* Main Content */}
              <div className="flex gap-4 mb-4">
                {/* Image */}
                <div className="relative flex-shrink-0">
                  <Image
                    width={100}
                    height={100}
                    src={baseURL + row.imageUrl}
                    alt={row.itemName}
                    className="rounded-xl object-cover w-24 h-24"
                    loading="lazy"
                  />
                  {row.creator?.username && (
                    <div
                      className="absolute -right-1 -bottom-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-green-500 shadow-lg"
                      title="Verified Collection"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width={14}
                        height={14}
                        className="fill-white"
                      >
                        <path fill="none" d="M0 0h24v24H0z" />
                        <path d="M10 15.172l9.192-9.193 1.415 1.414L10 18l-6.364-6.364 1.414-1.414z" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Title and Creator */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-lg text-jacarta-700 dark:text-white mb-2 leading-tight">
                    {row.itemName}
                  </h3>

                  {/* Creator Info */}
                  {row.creator && (
                    <div className="flex items-center gap-2 mb-3">
                      <div className="relative">
                        {row.creator.avatarUrl ? (
                          <img
                            src={`${baseURL}/${row.creator.avatarUrl}`}
                            alt={row.creator.username || "Creator"}
                            className="w-5 h-5 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-5 h-5 bg-accent rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-xs font-medium">
                              {row.creator.username?.charAt(0)?.toUpperCase() ||
                                "U"}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-xs text-jacarta-500 dark:text-jacarta-400 block">
                          Creator
                        </span>
                        <span className="text-sm font-medium text-jacarta-700 dark:text-white truncate block">
                          {row.creator.username || "Unknown Creator"}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats and Action */}
              <div className="border-t border-jacarta-100 dark:border-jacarta-600 pt-4">
                <div className="flex items-center justify-between">
                  {/* Stats */}
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-5 h-5 text-accent"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      <div>
                        <span className="text-sm font-semibold text-jacarta-700 dark:text-white block">
                          {row.viewsCount || 0}
                        </span>
                        <span className="text-xs text-jacarta-500 dark:text-jacarta-400">
                          Views
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Buy Button */}
                  <button
                    type="button"
                    className="bg-accent hover:bg-accent-dark text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                    onClick={(e) => handleActionClick(e, row)}
                  >
                    <span className="text-sm">
                      {isOwnedByViewer(row) ? "View Details" : "Buy Now"}
                    </span>
                  </button>
                </div>
              </div>
            </Link>
          ))}
      </div>
    );
  }

  if (viewType === "tablet") {
    // Tablet Compact Table View
    return (
      <>
        {!!assetsList?.length &&
          assetsList.map((row, index) => (
            <Link
              href={`/marketplace/${row._id}`}
              key={row._id}
              className="flex bg-white dark:bg-jacarta-700 border border-jacarta-100 dark:border-jacarta-600 rounded-lg mb-2 transition-shadow duration-200 hover:shadow-md"
              role="row"
            >
              {/* Art & Title - 45% */}
              <div className="flex w-[45%] items-center py-4 px-4" role="cell">
                <span className="text-jacarta-500 dark:text-jacarta-400 font-medium text-sm mr-3 min-w-[20px] flex-shrink-0">
                  {startIndex + index + 1}.
                </span>
                <figure className="relative mr-3 w-12 shrink-0 self-start">
                  <Image
                    width={48}
                    height={48}
                    src={baseURL + row.imageUrl}
                    alt={`avatar ${row.itemName}`}
                    className="rounded-lg object-cover w-full h-full"
                    loading="lazy"
                  />
                  {row.creator?.username && (
                    <div
                      className="absolute -right-1 -bottom-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-green-500 shadow-lg"
                      title="Verified Collection"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width={12}
                        height={12}
                        className="fill-white"
                      >
                        <path fill="none" d="M0 0h24v24H0z" />
                        <path d="M10 15.172l9.192-9.193 1.415 1.414L10 18l-6.364-6.364 1.414-1.414z" />
                      </svg>
                    </div>
                  )}
                </figure>
                <div className="min-w-0 flex-1">
                  <span
                    className="font-semibold text-sm text-jacarta-700 dark:text-white hover:text-accent dark:hover:text-accent transition-colors duration-200 block truncate"
                    title={row.itemName}
                  >
                    {row.itemName}
                  </span>

                  {/* Campaign Info */}
                  {row.impactInitiativeName && (
                    <div className="flex items-center gap-1 mt-1">
                      <div className="w-3 h-3 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-medium">
                          C
                        </span>
                      </div>
                      <span className="text-xs text-green-600 dark:text-green-400 truncate">
                        {row.impactInitiativeName}
                      </span>
                    </div>
                  )}

                  {/* Creator Info */}
                  <div className="flex items-center gap-1 mt-1">
                    <div className="w-3 h-3 bg-accent rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-medium">
                        {row.creator.username?.charAt(0)?.toUpperCase() || "U"}
                      </span>
                    </div>
                    <span className="text-xs text-jacarta-500 dark:text-jacarta-300 truncate">
                      {row.creator.username}
                    </span>
                  </div>
                </div>
              </div>

              {/* Price - 18% */}
              <div
                className="flex justify-center items-center w-[18%] whitespace-nowrap py-4 px-4"
                role="cell"
              >
                <div className="bg-jacarta-50 dark:bg-jacarta-600 rounded-lg px-3 py-2">
                  <span className="text-lg font-bold text-accent">
                    ${row.priceUsd}
                  </span>
                  <span className="text-xs text-jacarta-400 dark:text-jacarta-400 block text-center">
                    USD
                  </span>
                </div>
              </div>

              {/* Stats (Views) - 18% */}
              <div
                className="flex justify-center items-center w-[18%] py-4 px-4"
                role="cell"
              >
                <div className="flex items-center gap-1">
                  <svg
                    className="w-3 h-3 text-accent"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  <span className="text-xs font-medium text-jacarta-500 dark:text-jacarta-300">
                    {row.viewsCount || 0}
                  </span>
                </div>
              </div>

              {/* Action - 19% */}
              <div
                className="flex justify-center w-[19%] items-center py-4 px-4"
                role="cell"
              >
                <button
                  type="button"
                  className="bg-accent hover:bg-accent-dark text-white font-medium py-2 px-3 rounded-lg transition-colors duration-200 text-xs"
                  onClick={(e) => handleActionClick(e, row)}
                >
                  {isOwnedByViewer(row) ? "VIEW DETAILS" : "BUY NOW"}
                </button>
              </div>
            </Link>
          ))}
      </>
    );
  }

  // Desktop Table View
  return (
    <>
      {!!assetsList?.length &&
        assetsList.map((row, index) => (
          <Link
            href={`/marketplace/${row._id}`}
            key={row._id}
            className="flex items-center bg-white dark:bg-jacarta-700 border-b border-jacarta-100 dark:border-jacarta-600 py-3 transition-colors duration-200 hover:bg-jacarta-50 dark:hover:bg-jacarta-600"
            role="row"
          >
            <div className="flex w-[35%] items-center py-4 px-4" role="cell">
              <span className="text-jacarta-500 dark:text-jacarta-400 font-medium text-sm mr-3 lg:mr-5 min-w-[20px] flex-shrink-0">
                {startIndex + index + 1}.
              </span>
              <figure className="relative mr-2 w-10 shrink-0 self-start lg:mr-5 lg:w-12">
                <Image
                  width={48}
                  height={48}
                  src={baseURL + row.imageUrl}
                  alt={`avatar ${row.itemName}`}
                  className="rounded-lg object-cover w-full h-full"
                  loading="lazy"
                />
                {row.creator?.username && (
                  <div
                    className="absolute -right-1 -bottom-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-green-500 shadow-lg"
                    title="Verified Collection"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width={12}
                      height={12}
                      className="fill-white"
                    >
                      <path fill="none" d="M0 0h24v24H0z" />
                      <path d="M10 15.172l9.192-9.193 1.415 1.414L10 18l-6.364-6.364 1.414-1.414z" />
                    </svg>
                  </div>
                )}
              </figure>
              <div className="min-w-0 flex-1">
                <span
                  className="font-semibold text-sm text-jacarta-700 dark:text-white hover:text-accent dark:hover:text-accent transition-colors duration-200 block truncate"
                  title={row.itemName}
                >
                  {row.itemName}
                </span>
                {/* Campaign Info */}
                {row.impactInitiativeName && (
                  <div className="flex items-center gap-1 mt-1">
                    <div className="w-3 h-3 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">C</span>
                    </div>
                    <span className="text-xs text-green-600 dark:text-green-400 truncate">
                      {row.impactInitiativeName}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div
              className="flex justify-center items-center w-[13%] whitespace-nowrap py-4 px-4"
              role="cell"
            >
              <div className="bg-jacarta-50 dark:bg-jacarta-600 rounded-lg px-3 py-2">
                <span className="text-lg font-bold text-accent">
                  ${row.priceUsd}
                </span>
                <span className="text-xs text-jacarta-400 dark:text-jacarta-400 block text-center">
                  USD
                </span>
              </div>
            </div>
            <div
              className="flex justify-center w-[13%] items-center py-4 px-4"
              role="cell"
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-6 h-6 bg-accent rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs font-medium">
                    {row.creator.username?.charAt(0)?.toUpperCase() || "U"}
                  </span>
                </div>
                <span
                  className="text-sm font-medium text-jacarta-500 dark:text-jacarta-300 truncate"
                  title={row.creator.username}
                >
                  {row.creator.username}
                </span>
              </div>
            </div>
            <div
              className="flex justify-center w-[13%] items-center py-4 px-4"
              role="cell"
            >
              <div className="flex items-center gap-1">
                <svg
                  className="w-4 h-4 text-accent"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                <span className="text-sm font-medium text-jacarta-500 dark:text-jacarta-300">
                  {row.viewsCount || 0}
                </span>
              </div>
            </div>
            <div
              className="flex justify-center w-[13%] items-center py-4 px-4"
              role="cell"
            >
              <button
                type="button"
                className="bg-accent hover:bg-accent-dark text-white font-medium py-2 px-3 rounded-lg transition-colors duration-200 text-xs"
                onClick={(e) => handleActionClick(e, row)}
              >
                {isOwnedByViewer(row) ? "VIEW DETAILS" : "BUY NOW"}
              </button>
            </div>
          </Link>
        ))}
    </>
  );
};

export default ImpactListItems;
